import gql from 'graphql-tag';

import GraphQLClient from '../../adapter/GraphQLClient';
import HttpClient from '../../adapter/HttpClient';
import Invitation from '../../entity/Invitation';
import Membership from '../../entity/Membership';
import { Role } from '../../entity/MembershipRole';
import Organization from '../../entity/Organization';
import { PermissionAction } from '../../entity/Organization';
import Tag from '../../entity/Tag';

class OrganizationWebApi {
  private graphqlClient: GraphQLClient;
  private httpClient: HttpClient;

  constructor({ idGraphqlClient, httpClient }: { idGraphqlClient: GraphQLClient; httpClient: HttpClient }) {
    this.graphqlClient = idGraphqlClient;
    this.httpClient = httpClient;
  }

  public getOrganizations = async (permissionActions: PermissionAction[]): Promise<Organization[]> => {
    const result = await this.graphqlClient.query(
      gql`
        query organizations($permissionActions: [OrganizationPermissionActionEnum!]) {
          organizations(permissionActions: $permissionActions) {
            id
            name
            comment
            profileImageUrl
            permissions {
              action
              permission
            }
            appVersion
            tags {
              name
              label
            }
            isChildren
            businessRegion
          }
        }
      `,
      { permissionActions },
    );
    return result.organizations.map((o: any) => Organization.fromJSON(o) as Organization);
  };

  public uploadAvatarImage = async (organizationId: string, file: File): Promise<void> => {
    const formData = new FormData();

    formData.append('id', organizationId);
    formData.append('organization[avatar_image]', file);

    await this.httpClient.post('/organization_avatar_image', formData);
  };

  public updateGeneral = async ({
    id,
    name,
    comment,
    tags,
  }: {
    id: string;
    name: string;
    comment?: string;
    tags: Tag[];
  }): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation UpdateOrganization($id: ID!, $name: String!, $comment: String, $tags: [String!]) {
          updateOrganization(input: { id: $id, name: $name, comment: $comment, tags: $tags }) {
            organization {
              id
            }
          }
        }
      `,
      { id, name, comment: comment || null, tags: tags.map(tag => tag.label) },
    );
  };

  public createInvitation = async ({
    id,
    email,
    roles,
  }: {
    id: string;
    email: string;
    roles: Role[];
  }): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation createOrganizationInvitation($id: ID!, $email: String!, $roles: [MembershipRoleEnum!]!) {
          createOrganizationInvitation(input: { organizationId: $id, email: $email, roles: $roles }) {
            id
          }
        }
      `,
      { id, email, roles },
    );
  };

  public createMembership = async ({
    id,
    email,
    roles,
  }: {
    id: string;
    email: string;
    roles: Role[];
  }): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation createMembership($id: ID!, $email: String!, $roles: [MembershipRoleEnum!]!) {
          createMembership(input: { organizationId: $id, email: $email, roles: $roles }) {
            membership {
              id
            }
          }
        }
      `,
      { id, email, roles },
    );
  };

  public getMemberships = async (organizationId: string): Promise<Membership[]> => {
    const result = await this.graphqlClient.query(
      gql`
        query memberships($organizationId: ID!) {
          memberships(organizationId: $organizationId) {
            nodes {
              id
              user {
                id
                name
                email
                profileImageUrl
              }
              membershipRoleAssignments {
                id
                role
              }
            }
          }
        }
      `,
      { organizationId },
    );
    return result.memberships.nodes.map((m: any) => Membership.fromJSON(m));
  };

  public getMembership = async (id: string): Promise<Membership> => {
    const result = await this.graphqlClient.query(
      gql`
        query membership($id: ID!) {
          membership(id: $id) {
            id
            user {
              id
              name
              email
              profileImageUrl
            }
            membershipRoleAssignments {
              id
              role
            }
          }
        }
      `,
      { id },
    );
    return Membership.fromJSON(result.membership);
  };

  public deleteMembership = async (id: string): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation deleteMembership($id: ID!) {
          deleteMembership(input: { id: $id }) {
            id
          }
        }
      `,
      { id },
    );
  };

  public createMembershipRoleAssignment = async (membershipId: string, role: Role): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation createMembershipRoleAssignment($membershipId: ID!, $role: MembershipRoleEnum!) {
          createMembershipRoleAssignment(input: { membershipId: $membershipId, role: $role }) {
            id
          }
        }
      `,
      { membershipId, role },
    );
  };

  public deleteMembershipRoleAssignment = async (id: string): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation deleteMembershipRoleAssignment($id: ID!) {
          deleteMembershipRoleAssignment(input: { id: $id }) {
            id
          }
        }
      `,
      { id },
    );
  };

  public getInvitations = async (organizationId: string): Promise<Invitation[]> => {
    const result = await this.graphqlClient.query(
      gql`
        query organizationInvitations($organizationId: ID!) {
          organizationInvitations(organizationId: $organizationId) {
            nodes {
              id
              email
              roles
              expiresAt
            }
          }
        }
      `,
      { organizationId },
    );
    return result.organizationInvitations.nodes.map((m: any) => Invitation.fromJSON(m));
  };

  public deleteInvitation = async (id: string): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation deleteOrganizationInvitation($id: ID!) {
          deleteOrganizationInvitation(input: { id: $id }) {
            id
          }
        }
      `,
      { id },
    );
  };
}

export default OrganizationWebApi;
